import React from 'react';

import type { CompositionV3DataQuery } from '@revelio/data-access';
import {
  type BreadcrumbSegment,
  FormatType,
  getFormatter,
  getPrimaryColor,
  getPrimaryColorDark,
  TreeMap as ReplotsTreeMap,
  type TreeMapData,
  useChartTheme,
} from '@revelio/replots';

import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import {
  getTreemapDrilldownIsLeafLevel,
  getTreemapInitialLevelOffset,
  isFlatTreemapSegment,
  isOverlappingTreemapSegment,
  isTreemapDrilldownSegment,
  type TreemapDrilldownSegment,
} from '../../filters/utils/treemapDrilldownLevels';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import { convertCompositionDataV3 } from '../DataProvider/composition/composition-v3/convert-composition-data-v3';
import {
  type DrilldownFilter,
  NestedCompositionTreemapDrilldownProvider,
} from '../DataProvider/composition/NestedCompositionTreemapDrilldownProvider';
import {
  type BreakdownSingleDataSet,
  DataProviderQueryStatus,
  DataType,
  isLeveledBreakdownDataSet,
  isNestedBreakdownDataSet,
  type SingleDataPoint,
  type SingleDataSet,
} from '../DataProvider/types';
import { tableToBreakdownSingleDataSet } from '../DataProvider/utils/table-to-breakdown-single-data-set';
import { findLadderLevel } from '../DataResolver/taxonomy-levels';
import { useCompositionV4Drilldown } from '../DataResolver/use-composition-v4-drilldown';
import {
  usePageConfig,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import { PageType } from '../PageConfigProvider/types';
import type { TreemapTaxonomyCoverage } from '../PageConfigProvider/types/item';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import { useDrilldownHeaderBreadcrumb } from './ChartHeaderBreadcrumb';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import { SharableChart } from './chart-menu/plot-share/SharableChart';
import {
  buildNestedTreemapChildren,
  buildTreemapFromLevels,
  type FlatTreemapItem,
  type NestedTreemapParent,
  type NestedTreemapParentTotal,
  nestedTreemapLookupKey,
  taxonomyCoverageMaxGroups,
} from './utils/buildNestedTreemapData';
import {
  compositionDrilldownOtherColor,
  type DrilldownEntry,
  drilldownEntryToFilter,
  getDrilldownColor,
} from './utils/composition-drilldown';
import { getFormat } from './utils/getFormat';
import { isOtherSegment } from './utils/group-and-filter-data';

// Root breadcrumb label per drill segment (e.g. geography → "All Geographies"),
// so the "back to root" crumb names what it resets to.
const ALL_LABEL_BY_SEGMENT: Record<TreemapDrilldownSegment, string> = {
  geography: 'All Geographies',
  role: 'All Roles',
  rics: 'All Industries',
  skills: 'All Skills',
  activities: 'All Activities',
};

const sortOtherLast = <T extends { name: string; id?: string }>(
  items: T[]
): T[] =>
  [...items].sort((a, b) => {
    const aIsOther = isOtherSegment({ dim: a.name, id: a.id });
    const bIsOther = isOtherSegment({ dim: b.name, id: b.id });
    if (aIsOther && !bIsOther) return 1;
    if (!aIsOther && bIsOther) return -1;
    return 0;
  });

function buildTreemapChildren(
  data: BreakdownSingleDataSet['data'] | SingleDataSet['data']
): TreeMapData['children'] {
  const isSegmented =
    data.length > 0 && data[0] !== undefined && 'breakdown' in data[0];
  const breakdownData = data as BreakdownSingleDataSet['data'];
  const singleEntity =
    breakdownData.length === 1 ? breakdownData[0] : undefined;

  if (singleEntity !== undefined && singleEntity !== null && isSegmented) {
    return sortOtherLast(
      singleEntity.breakdown.map((b) => ({
        name: b.dim,
        value: b.value ?? 0,
        id: b.drilldownId ?? b.id ?? undefined,
        share: b.share,
      }))
    );
  }

  if (isSegmented) {
    return sortOtherLast(
      breakdownData.map((entity) => ({
        name: entity.dim,
        value: entity.breakdown.reduce(
          (sum: number, b: SingleDataPoint) => sum + (b.value ?? 0),
          0
        ),
        children: entity.breakdown.map((b) => ({
          name: b.dim,
          value: b.value ?? 0,
          parentName: entity.dim,
          id: b.drilldownId ?? b.id ?? undefined,
          share: b.share,
        })),
      }))
    );
  }

  return (data as SingleDataSet['data']).map((entity) => ({
    name: entity.dim,
    value: entity.value ?? 0,
  }));
}

function nestFlatChildren(
  children: TreeMapData['children'],
  parentLookup: Map<string, NestedTreemapParent> | null,
  parentTotals?: Map<string, NestedTreemapParentTotal>,
  omitStandaloneOther?: boolean,
  taxonomyCoverage?: TreemapTaxonomyCoverage
): TreeMapData['children'] {
  if (parentLookup === null || children.length === 0) return children;

  const alreadyNested = children.some(
    (c) => Array.isArray(c.children) && c.children.length > 0
  );
  if (alreadyNested) return children;

  const flatItems: FlatTreemapItem[] = children.map((c) => ({
    id: String(c.id ?? c.name),
    name: c.name,
    value: c.value ?? 0,
    share: c.share,
  }));
  const nested = buildNestedTreemapChildren(
    flatItems,
    parentLookup,
    parentTotals,
    {
      omitStandaloneOther,
      omitOther: taxonomyCoverage?.omitOther,
      maxGroups: taxonomyCoverageMaxGroups(taxonomyCoverage),
    }
  );

  const hasGroup = nested.some(
    (node) => Array.isArray(node.children) && node.children.length > 0
  );
  if (!hasGroup) return children;

  return nested;
}

type CompositionV3Payload = NonNullable<
  CompositionV3DataQuery['composition_v3']
>;

type TreemapContentProps = {
  compositionData: CompositionV3Payload | null;
  monthRefs: string[];
  secondaryDimension: TreemapDrilldownSegment;
  drilldown: DrilldownFilter | null;
  parentLookup: Map<string, NestedTreemapParent> | null;
  parentTotals: Map<string, NestedTreemapParentTotal>;
  metricToUse: MetricValues | undefined;
  format: ReturnType<typeof getFormat>;
  drilledGroupId?: string;
  treemapSizeTransform?: 'none' | 'sqrt';
  taxonomyCoverage?: TreemapTaxonomyCoverage;
  colorPalette: string[] | undefined;
  colorOverride: { Other: string } | undefined;
  onCellClick?: (
    cellId: string,
    cellName: string,
    parentName?: string,
    color?: string
  ) => void;
  onHeaderClick?: (groupId: string, groupName: string, color?: string) => void;
  breadcrumbPath?: BreadcrumbSegment[];
  onBreadcrumbClick?: (targetDepth: number) => void;
  /** Reports the formatted view total for the header breadcrumb. */
  onTotalLabelChange?: (label: string | null) => void;
};

function TreemapContent({
  compositionData,
  monthRefs,
  secondaryDimension,
  drilldown,
  parentLookup,
  parentTotals,
  metricToUse,
  format,
  drilledGroupId,
  treemapSizeTransform,
  taxonomyCoverage,
  colorPalette,
  colorOverride,
  onCellClick,
  onHeaderClick,
  breadcrumbPath,
  onBreadcrumbClick,
  onTotalLabelChange,
}: TreemapContentProps) {
  const isCompanyEntitiesView =
    secondaryDimension === 'rics' &&
    drilldown?.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company') &&
    compositionData !== null &&
    compositionData.entities?.length > 0;

  // No child payload for this view: the query is in flight, or it failed.
  const detailUnavailable = compositionData === null;

  const treemapChildren = React.useMemo(() => {
    const effectiveMetric = metricToUse ?? 'headcount';
    if (compositionData === null) {
      return [];
    }
    if (isCompanyEntitiesView) {
      const metricKey =
        effectiveMetric as keyof CompositionV3Payload['entities'][number]['metrics'];
      return compositionData.entities
        .map((entity) => {
          const metricData = entity.metrics?.[metricKey];
          const lastPoint = Array.isArray(metricData)
            ? metricData[metricData.length - 1]
            : undefined;
          const rawValue =
            lastPoint && typeof lastPoint === 'object' && 'val' in lastPoint
              ? (lastPoint as { val?: number }).val
              : undefined;
          const value =
            typeof rawValue === 'number' && Number.isFinite(rawValue)
              ? rawValue
              : 0;
          const name = entity.short_name ?? '';
          if (!name) return null;
          return {
            name,
            value,
            id: entity.id != null ? String(entity.id) : undefined,
          };
        })
        .filter((c): c is NonNullable<typeof c> => c !== null);
    }
    const drilldownDataSet = convertCompositionDataV3({
      metric: effectiveMetric as Parameters<
        typeof convertCompositionDataV3
      >[0]['metric'],
      segment: secondaryDimension,
      data: compositionData,
      monthRefs,
    });

    if (drilldownDataSet.type !== DataType.BREAKDOWN_SINGLE) {
      return null;
    }

    return buildTreemapChildren(
      drilldownDataSet.data as BreakdownSingleDataSet['data']
    ).filter((child) => (child.name ?? '').trim() !== '');
  }, [
    isCompanyEntitiesView,
    compositionData,
    monthRefs,
    metricToUse,
    secondaryDimension,
  ]);

  const nestedChildren = React.useMemo(() => {
    if (treemapChildren === null) return null;
    if (detailUnavailable && parentLookup !== null && parentTotals.size > 0) {
      return buildNestedTreemapChildren([], parentLookup, parentTotals, {
        omitStandaloneOther: isOverlappingTreemapSegment(secondaryDimension),
        omitOther: taxonomyCoverage?.omitOther,
        maxGroups: taxonomyCoverageMaxGroups(taxonomyCoverage),
      });
    }
    return nestFlatChildren(
      treemapChildren,
      parentLookup,
      parentTotals,
      isOverlappingTreemapSegment(secondaryDimension),
      taxonomyCoverage
    );
  }, [
    treemapChildren,
    detailUnavailable,
    parentLookup,
    parentTotals,
    secondaryDimension,
    taxonomyCoverage,
  ]);

  return (
    <TreemapPlot
      nestedChildren={nestedChildren}
      format={format}
      treemapSizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
      onCellClick={onCellClick}
      onHeaderClick={onHeaderClick}
      breadcrumbPath={breadcrumbPath}
      onBreadcrumbClick={onBreadcrumbClick}
      onTotalLabelChange={onTotalLabelChange}
      detailLoading={detailUnavailable}
      drilledGroupId={detailUnavailable ? undefined : drilledGroupId}
    />
  );
}

type TreemapPlotProps = Pick<
  TreemapContentProps,
  | 'format'
  | 'treemapSizeTransform'
  | 'colorPalette'
  | 'colorOverride'
  | 'onCellClick'
  | 'onHeaderClick'
  | 'breadcrumbPath'
  | 'onBreadcrumbClick'
  | 'onTotalLabelChange'
  | 'drilledGroupId'
> & {
  nestedChildren: TreeMapData['children'] | null;
  detailLoading?: boolean;
};

function TreemapPlot({
  nestedChildren,
  format,
  treemapSizeTransform,
  colorPalette,
  colorOverride,
  onCellClick,
  onHeaderClick,
  breadcrumbPath,
  onBreadcrumbClick,
  onTotalLabelChange,
  detailLoading,
  drilledGroupId,
}: TreemapPlotProps) {
  // Percentage-formatted views always total 100%.
  const totalLabel = React.useMemo(() => {
    if (nestedChildren === null || nestedChildren.length === 0) return null;
    const sumLeaves = (nodes: TreeMapData['children']): number =>
      nodes.reduce((sum, node) => {
        if (Array.isArray(node.children) && node.children.length > 0) {
          return sum + sumLeaves(node.children);
        }
        return sum + Math.max(0, node.value ?? 0);
      }, 0);
    const formatTotal = getFormatter(format);
    return format === FormatType.PERCENTAGE
      ? formatTotal(1)
      : formatTotal(sumLeaves(nestedChildren));
  }, [nestedChildren, format]);

  React.useEffect(() => {
    onTotalLabelChange?.(totalLabel);
  }, [totalLabel, onTotalLabelChange]);

  if (nestedChildren === null) return null;

  const treeData: TreeMapData = { name: 'root', children: nestedChildren };

  return (
    <SharableChart
      Component={ReplotsTreeMap}
      data={treeData}
      format={format}
      sizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
      onCellClick={onCellClick}
      onHeaderClick={onHeaderClick}
      onBreadcrumbClick={onBreadcrumbClick}
      breadcrumbPath={breadcrumbPath}
      detailLoading={detailLoading}
      drilledGroupId={drilledGroupId}
    />
  );
}

type DrilldownTreeMapProps = Pick<
  TreemapContentProps,
  | 'format'
  | 'treemapSizeTransform'
  | 'taxonomyCoverage'
  | 'colorPalette'
  | 'colorOverride'
> & {
  dataSet: BreakdownSingleDataSet | SingleDataSet;
  metric: MetricValues | undefined;
  primaryDimension: PrimaryFilterId;
  secondaryDimension: TreemapDrilldownSegment;
};

const DrilldownTreeMap = ({
  dataSet,
  metric,
  primaryDimension,
  secondaryDimension,
  format,
  treemapSizeTransform,
  taxonomyCoverage,
  colorPalette,
  colorOverride,
}: DrilldownTreeMapProps) => {
  const effectiveFilters = useEffectiveFilters();
  const { geographySelectionListIds } = useFilterContext();

  const [drilldownStack, setDrilldownStack] = React.useState<DrilldownEntry[]>(
    []
  );

  const initialLevelOffset = getTreemapInitialLevelOffset(
    secondaryDimension,
    effectiveFilters,
    geographySelectionListIds
  );

  const geographyLadderKey = geographySelectionListIds.join(',');
  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  React.useEffect(() => {
    setDrilldownStack([]);
  }, [
    primaryDimension,
    secondaryDimension,
    initialLevelOffset,
    geographyLadderKey,
  ]);

  const [drillTotalLabel, setDrillTotalLabel] = React.useState<string | null>(
    null
  );

  const handleBreadcrumbClick = React.useCallback((targetDepth: number) => {
    setDrillTotalLabel(null);
    setDrilldownStack((prev) => prev.slice(0, targetDepth));
  }, []);

  // The drilldown breadcrumb renders in the chart card header (Figma
  // 1096:20975) instead of inside the plot; contexts with no header listening
  // (expand modal, shared report) keep the in-plot crumbs.
  const headerBreadcrumbActive = useDrilldownHeaderBreadcrumb({
    stack: drilldownStack,
    totalLabel: drillTotalLabel,
    onNavigate: handleBreadcrumbClick,
  });

  // Once drilled, the whole view takes the colour of the drilled-into parent
  // ("Other" keeps its grey via colorOverride).
  const drilldownColor = React.useMemo(
    () => getDrilldownColor(drilldownStack),
    [drilldownStack]
  );
  const drilledPalette =
    drilldownStack.length > 0 && drilldownColor !== undefined
      ? [drilldownColor]
      : colorPalette;

  const drilledInto = drilldownStack[drilldownStack.length - 1];
  const drilledMetric = metric ?? 'headcount';
  const metrics = React.useMemo<MetricValues[]>(
    () => [drilledMetric, 'share_of_headcount'],
    [drilledMetric]
  );
  const drilled = useCompositionV4Drilldown({
    primaryDimension,
    dimension: secondaryDimension,
    drilledInto: taxonomyCoverage?.topN ? undefined : drilledInto,
    metrics,
    sublevel: !isFlatTreemapSegment(secondaryDimension),
  });
  const drilledDataSet = React.useMemo(
    () =>
      drilled?.status === DataProviderQueryStatus.Success && drilled.data
        ? tableToBreakdownSingleDataSet(drilled.data, drilledMetric)
        : undefined,
    [drilled, drilledMetric]
  );
  const viewDataSet = drilledInto === undefined ? dataSet : drilledDataSet;

  const leveledTreemap = React.useMemo(() => {
    if (viewDataSet && isNestedBreakdownDataSet(viewDataSet)) {
      return buildTreemapFromLevels(viewDataSet, {
        coverage: taxonomyCoverage,
        omitStandaloneOther: isOverlappingTreemapSegment(secondaryDimension),
      });
    }
    if (viewDataSet && isLeveledBreakdownDataSet(viewDataSet)) {
      return {
        children: buildTreemapChildren(viewDataSet.data).filter(
          (child) => (child.name ?? '').trim() !== ''
        ),
        parentLookup: null,
        parentLevel: undefined,
        childLevel: viewDataSet.level,
      };
    }
    return null;
  }, [viewDataSet, secondaryDimension, taxonomyCoverage]);

  const drilldown =
    drilldownStack.length > 0 ? drilldownEntryToFilter(drilldownStack) : null;
  // Crumb i navigates to the view drilled into stack entry i, so its
  // targetDepth (the slice end) is i + 1; the root crumb ("All Roles",
  // "All Geographies", …) resets to the top level for this segment.
  const breadcrumbPath: BreadcrumbSegment[] =
    drilldownStack.length === 0
      ? []
      : [
          { name: ALL_LABEL_BY_SEGMENT[secondaryDimension], targetDepth: 0 },
          ...drilldownStack.map((e, i) => ({
            name: e.name,
            targetDepth: i + 1,
          })),
        ];

  const pushDrill = (
    levelId: string,
    cellId: string,
    cellName: string,
    color?: string,
    parent?: { levelId: string; id: string; name: string }
  ) => {
    if (cellName.toLowerCase() === 'other') return;
    const needsNumericId =
      secondaryDimension === 'geography' ||
      secondaryDimension === 'rics' ||
      secondaryDimension === 'activities';
    const id = needsNumericId ? parseInt(cellId, 10) : cellId;
    if (needsNumericId && !Number.isFinite(id as number)) return;
    const parentId = (() => {
      if (parent === undefined) return undefined;
      return needsNumericId ? parseInt(parent.id, 10) : parent.id;
    })();
    setDrillTotalLabel(null);
    setDrilldownStack((prev) => {
      const top = prev[prev.length - 1];
      if (
        top !== undefined &&
        top.level === levelId &&
        String(top.id) === String(id)
      ) {
        return prev;
      }
      const entries: DrilldownEntry[] = [];
      // Pushing the parent first makes a cell drill equivalent to
      // header-then-cell, so the breadcrumb reads "Parent > Cell".
      // Skip it when the parent is already the top entry (e.g. a
      // click landing on a stale view right after a header drill)
      // so the stack never holds the same parent twice.
      const topIsParent =
        top !== undefined &&
        parent !== undefined &&
        top.level === parent.levelId &&
        String(top.id) === String(parentId);
      if (
        !topIsParent &&
        parent !== undefined &&
        parentId !== undefined &&
        (!needsNumericId || Number.isFinite(parentId as number))
      ) {
        entries.push({
          level: parent.levelId,
          id: parentId,
          name: parent.name,
          segment: secondaryDimension,
          color,
        });
      }
      entries.push({
        level: levelId,
        id,
        name: cellName,
        segment: secondaryDimension,
        color,
      });
      return [...prev, ...entries];
    });
  };

  const drillHandlers = ({
    isDisplayLeaf,
    displayLevelId,
    parentLevelId,
    parentLookup,
    groupsAreParents = true,
  }: {
    isDisplayLeaf: boolean;
    displayLevelId: string;
    parentLevelId: string | null;
    parentLookup: Map<string, NestedTreemapParent> | null;
    groupsAreParents?: boolean;
  }) => ({
    handleCellClick: isDisplayLeaf
      ? undefined
      : (
          cellId: string,
          cellName: string,
          _parentName?: string,
          color?: string
        ) => {
          const parent =
            parentLevelId === null || parentLookup === null
              ? undefined
              : parentLookup.get(nestedTreemapLookupKey(cellId));
          pushDrill(
            displayLevelId,
            cellId,
            cellName,
            color,
            parent === undefined || parentLevelId === null
              ? undefined
              : {
                  levelId: parentLevelId,
                  id: parent.parentId,
                  name: parent.parentName,
                }
          );
        },
    handleHeaderClick:
      parentLevelId === null || !groupsAreParents
        ? undefined
        : (groupId: string, groupName: string, color?: string) =>
            pushDrill(parentLevelId, groupId, groupName, color),
  });

  const loader = (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minHeight: 0,
      }}
    >
      <ChartLoader />
    </div>
  );

  const leveledView = (() => {
    if (viewDataSet === undefined || leveledTreemap === null) return null;
    const { childLevel, parentLevel, parentLookup } = leveledTreemap;
    const parentLevelId =
      (parentLevel &&
        findLadderLevel(parentLevel, geographySelectionListIds)?.id) ??
      null;
    const childLevelId = findLadderLevel(
      childLevel,
      geographySelectionListIds
    )?.id;
    const { handleCellClick, handleHeaderClick } = drillHandlers({
      isDisplayLeaf:
        childLevelId === undefined ||
        getTreemapDrilldownIsLeafLevel(
          secondaryDimension,
          childLevelId,
          geographySelectionListIds
        ),
      displayLevelId: childLevelId ?? '',
      parentLevelId,
      parentLookup,
      groupsAreParents: viewDataSet.data.length === 1,
    });
    const drilledGroupId =
      drilledInto !== undefined && drilledInto.level === parentLevelId
        ? nestedTreemapLookupKey(drilledInto.id)
        : undefined;
    return (
      <div style={{ width: '100%', height: '100%' }}>
        <TreemapPlot
          nestedChildren={leveledTreemap.children}
          format={format}
          treemapSizeTransform={treemapSizeTransform}
          colorPalette={drilledPalette}
          colorOverride={colorOverride}
          onCellClick={handleCellClick}
          onHeaderClick={handleHeaderClick}
          breadcrumbPath={headerBreadcrumbActive ? undefined : breadcrumbPath}
          onBreadcrumbClick={handleBreadcrumbClick}
          onTotalLabelChange={
            headerBreadcrumbActive ? setDrillTotalLabel : undefined
          }
          drilledGroupId={drilledGroupId}
        />
      </div>
    );
  })();

  const drilledView = (() => {
    if (drilledInto === undefined || drilled === undefined) return null;
    return drilled.status === DataProviderQueryStatus.Fetching ||
      drilled.status === DataProviderQueryStatus.Idle
      ? loader
      : null;
  })();

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
      }}
    >
      <div
        style={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}
      >
        {leveledView ??
          drilledView ??
          (drilled === undefined || drilledInto === undefined ? (
            <NestedCompositionTreemapDrilldownProvider
              primaryDimension={primaryDimension}
              segment={secondaryDimension}
              drilldown={drilldown}
              taxonomyCoverage={taxonomyCoverage}
            >
              {({
                data: drilldownData,
                monthRefs,
                status,
                parentLookup,
                parentTotals,
                displayLevelId,
                isDisplayLeaf,
                parentLevelId,
              }) => {
                if (status === DataProviderQueryStatus.Fetching) {
                  return loader;
                }
                const compositionData = drilldownData ?? null;
                const showGroupsWithoutDetail =
                  compositionData === null &&
                  parentLookup !== null &&
                  parentTotals.size > 0;
                if (compositionData === null && !showGroupsWithoutDetail) {
                  return null;
                }

                const { handleCellClick, handleHeaderClick } = drillHandlers({
                  isDisplayLeaf,
                  displayLevelId,
                  parentLevelId,
                  parentLookup,
                });

                const drilldownLevelId = (() => {
                  if (drilldown === null) return null;
                  return 'levelId' in drilldown
                    ? drilldown.levelId
                    : drilldown.level;
                })();
                const drilledGroupId =
                  drilldown !== null && drilldownLevelId === parentLevelId
                    ? nestedTreemapLookupKey(String(drilldown.id))
                    : undefined;

                return (
                  <div style={{ width: '100%', height: '100%' }}>
                    <TreemapContent
                      compositionData={compositionData}
                      monthRefs={monthRefs}
                      secondaryDimension={secondaryDimension}
                      drilldown={drilldown}
                      parentLookup={parentLookup}
                      parentTotals={parentTotals}
                      metricToUse={metric}
                      format={format}
                      drilledGroupId={drilledGroupId}
                      treemapSizeTransform={treemapSizeTransform}
                      taxonomyCoverage={taxonomyCoverage}
                      colorPalette={drilledPalette}
                      colorOverride={colorOverride}
                      onCellClick={handleCellClick}
                      onHeaderClick={handleHeaderClick}
                      breadcrumbPath={
                        headerBreadcrumbActive ? undefined : breadcrumbPath
                      }
                      onBreadcrumbClick={handleBreadcrumbClick}
                      onTotalLabelChange={
                        headerBreadcrumbActive ? setDrillTotalLabel : undefined
                      }
                    />
                  </div>
                );
              }}
            </NestedCompositionTreemapDrilldownProvider>
          ) : null)}
      </div>
    </div>
  );
};

export const NestedTreeMap = ({
  data: dataSet,
  metric,
}: {
  data: BreakdownSingleDataSet | SingleDataSet;
  metric?: MetricValues;
}) => {
  const { data } = dataSet;
  const metricFromPlot = usePlotMetric();
  const metricToUse = metric ?? metricFromPlot;
  const { item } = useChart();
  const { primaryFilter } = useTabContentConfig();
  const primaryDimension = primaryFilter?.id;
  const secondaryDimension = item?.plot?.secondaryDimension;
  const { config: pageConfig } = usePageConfig();
  const isReport = pageConfig.type === PageType.REPORT;
  const { theme } = useChartTheme();
  const isDark = theme === 'dark';
  const colorPalette = isReport
    ? [isDark ? getPrimaryColorDark() : getPrimaryColor()]
    : (item?.plot?.colorPalette ?? undefined);
  const colorOverride = isReport
    ? undefined
    : { Other: compositionDrilldownOtherColor(theme) };

  const format = item?.plot?.format ?? getFormat({ metric: metricToUse });
  const treemapSizeTransform = item?.plot?.treemap?.sizeTransform;
  const taxonomyCoverage = item?.plot?.treemap?.taxonomyCoverage;

  const children = React.useMemo(() => buildTreemapChildren(data), [data]);
  const asTree: TreeMapData = React.useMemo(
    () => ({ name: 'Root', children }),
    [children]
  );

  if (
    isTreemapDrilldownSegment(secondaryDimension) &&
    primaryDimension !== undefined
  ) {
    return (
      <DrilldownTreeMap
        dataSet={dataSet}
        metric={metricToUse}
        primaryDimension={primaryDimension}
        secondaryDimension={secondaryDimension}
        format={format}
        treemapSizeTransform={treemapSizeTransform}
        taxonomyCoverage={taxonomyCoverage}
        colorPalette={colorPalette}
        colorOverride={colorOverride}
      />
    );
  }

  return (
    <SharableChart
      Component={ReplotsTreeMap}
      data={asTree}
      format={format}
      sizeTransform={treemapSizeTransform}
      colorPalette={colorPalette}
      colorOverride={colorOverride}
    />
  );
};
